import { Directive, ElementRef, booleanAttribute, inject, input } from '@angular/core';
import { focusControl, getActiveElement, getTabStops, indexOfTabStop, isPlainTab } from './focus-utils';

/**
 * Row/column aware `Tab` navigation for tables and grids.
 *
 * Pressing `Tab` moves focus to the next editable column within the current row. From the last editable column of a row
 * focus continues to the first applicable editable field of the next row. `Shift+Tab` mirrors the behaviour backwards.
 *
 * Disabled, hidden and read-only cells are skipped. When the user tabs past the last editable cell of the last row (or
 * before the very first one) the event is left untouched so focus leaves the grid exactly as the browser would do.
 *
 * Controls that are only programmatically focusable (`tabindex="-1"`, e.g. the non-selected members of a roving
 * tabindex radio group) are included so every editable column stays reachable.
 *
 * @example
 *   ```html
 *   <table hxGridTabNav>
 *     ...
 *   </table>
 *
 *   <!-- Custom row element -->
 *   <div hxGridTabNav rowSelector="hx-table-row">...</div>
 *   ```;
 */
@Directive({
  selector: '[hxGridTabNav]',
  standalone: true,
  host: {
    '(keydown)': 'handleKeydown($event)',
    '[attr.data-hx-tab-self-managed]': '!disabled() ? "" : null',
  },
})
export class HxGridTabNavigation {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Selector identifying a grid row inside the host. */
  readonly rowSelector = input<string>('hx-table-header, hx-table-row, tr, [role="row"]', { alias: 'hxGridTabNavRowSelector' });

  /** Disables the managed behaviour and falls back to the browser's native tab sequence. */
  readonly disabled = input(false, { alias: 'hxGridTabNavDisabled', transform: booleanAttribute });

  protected handleKeydown(event: KeyboardEvent): void {
    if (this.disabled() || !isPlainTab(event) || event.defaultPrevented) {
      return;
    }

    const active = getActiveElement();
    if (!active || !this.host.nativeElement.contains(active)) {
      return;
    }

    // Overlay-based editors (dropdown panels, date pickers) handle Tab themselves.
    if (active.closest('[data-hx-tab-self-managed]') !== this.host.nativeElement) {
      return;
    }

    const rows = this.getRows();
    const currentRow = rows.find((row) => row.contains(active));
    if (!currentRow) {
      return;
    }

    const delta = event.shiftKey ? -1 : 1;
    const cells = this.getCells(currentRow);
    const currentIndex = this.indexOfCurrentCell(currentRow, cells, active);
    if (currentIndex === -1) {
      return;
    }

    const nextIndex = currentIndex + delta;
    if (nextIndex >= 0 && nextIndex < cells.length) {
      this.moveTo(event, cells[nextIndex]);
      return;
    }

    // Row boundary: continue into the adjacent row.
    const rowIndex = rows.indexOf(currentRow);
    const target = this.findAdjacentRowCell(rows, rowIndex, delta);
    if (target) {
      this.moveTo(event, target);
    }
    // Otherwise the grid boundary was reached: leave the event alone so focus exits the grid normally.
  }

  /** Visible rows of the grid, in document order. */
  private getRows(): HTMLElement[] {
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(this.rowSelector())).filter(
      (row) => row.getClientRects().length > 0
    );
  }

  /**
   * The one focusable target per column of a row, in column order.
   *
   * Programmatically focusable controls are included so roving-tabindex cells (the non-selected radios of a group,
   * the `hx-radio` of a grid cell that has no group at all) stay reachable column by column.
   */
  private getCells(row: HTMLElement): HTMLElement[] {
    return getTabStops(row, 'dom', true);
  }

  /**
   * Index of the column that currently owns focus. Focus inside a composite control maps to that control's column, so
   * `Tab` leaves the whole widget instead of stepping through its remaining members.
   */
  private indexOfCurrentCell(row: HTMLElement, cells: HTMLElement[], active: HTMLElement): number {
    return indexOfTabStop(row, cells, active);
  }

  /** Walks forwards/backwards over rows until one with at least one editable control is found. */
  private findAdjacentRowCell(rows: HTMLElement[], fromRowIndex: number, delta: number): HTMLElement | undefined {
    for (let i = fromRowIndex + delta; i >= 0 && i < rows.length; i += delta) {
      const row = rows[i];
      if (!row) {
        continue;
      }
      const cells = this.getCells(row);
      if (cells.length === 0) {
        continue;
      }
      // Forward: first editable column of the next row. Backward: last editable column of the previous row.
      return delta > 0 ? cells[0] : cells[cells.length - 1];
    }
    return undefined;
  }

  private moveTo(event: KeyboardEvent, target: HTMLElement | undefined): void {
    if (!target) {
      return;
    }
    event.preventDefault();
    // Prevent an enclosing [hxTabNav] container from re-processing the same key press.
    event.stopPropagation();
    focusControl(target);
  }
}
